import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useAppDispatch } from '@/utils/typeHelpers'
import {
  openUploadModal,
  closeUploadModal,
  useFeatureDispatch,
  useFeatureState,
} from '../lib/state'
import { TabCategoryKeys as ModalTypes } from '../lib/constants'

import { useReselector } from '@/utils/sharedHooks'
import { DATE_FORMATS_LUXON } from '@/utils/dateHelpers'
import { UploadDocumentType } from '@/constants/businessConstants'
import { fetchTaxUserDocumentsIfNeeded } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'
import { fetchUserDocuments } from '../lib/userDocuments.slice'
import { useDocumentUpload } from '@/components/documents/upload/hooks/useDocumentUpload'
import { selectCurrentAnnualTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import { DocumentCategorySelect } from '@/components/form-fields'
import { DocumentSearchBarOnChangeData } from '../shared/DocumentsSearchBar'
import { selectUserDocumentCategoryByDocument } from '../lib/userDocuments.selector'
import { TabPaneToolbar } from '../shared/TabPaneToolbar'
import { RowTypes } from '../table/TableRows'
import { UserDocumentsTable } from '../table'
import { DocumentTabPane } from '.'
import { DocumentUploadDialog, Popup, Text } from '@/ui'
import { useAnalyticsTrack } from '@/features/amplitude'
import { UploadDocumentButton } from '../shared/UploadDocumentButton'

import { selectTaxUserDocuments } from '@/features/taxes/annual-taxes/taxUserDocuments.selector'
// TODO: Migrate DatePicker to V2
import { DatePicker } from '@/base'

const COLUMNS = [
  {
    sortBy: 'fileName',
    title: 'File Name',
  },
  {
    sortBy: 'description',
    title: 'Description',
  },
  {
    sortBy: 'tag',
    title: 'Form Type',
  },
  {
    sortBy: null,
    title: 'Actions',
  },
]

export const Tax: DocumentTabPane = ({ tableOptions, documents }) => {
  const dispatch = useFeatureDispatch()
  const track = useAnalyticsTrack()
  const appDispatch = useAppDispatch()
  const featureState = useFeatureState()
  const {
    activeUploadModal,
    selectedDocument,
    userDocumentCategoryId: stateUserDocumentCategoryId,
  } = featureState
  const isRefreshingAfterDelete = featureState.isRefreshingAfterDelete ?? false
  const [filteredDocuments, setFilteredDocuments] = useState(documents)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [searchParams] = useSearchParams()
  const taxUserDocuments = useReselector(selectTaxUserDocuments)
  const documentCategoryId = searchParams.get('documentCategoryId')
  const [userDocumentCategoryId, setUserDocumentCategoryId] = useState<
    number | null | undefined
  >(
    documentCategoryId
      ? Number(documentCategoryId)
      : stateUserDocumentCategoryId
  )
  const [year, setYear] = useState<string>()
  const currentTaxYear = useReselector(selectCurrentAnnualTaxYear)
  const nextTaxYear = (Number(currentTaxYear) + 1).toString()
  const category = useReselector(
    selectUserDocumentCategoryByDocument,
    selectedDocument || null
  )

  useEffect(() => {
    const documentCategoryId = searchParams.get('documentCategoryId')
    if (documentCategoryId)
      dispatch?.(openUploadModal(ModalTypes.TAX, parseInt(documentCategoryId)))
  }, [searchParams, dispatch])

  useEffect(() => {
    appDispatch(fetchTaxUserDocumentsIfNeeded())
  }, [appDispatch])

  useEffect(() => {
    if (!isRefreshing && !isRefreshingAfterDelete) {
      setFilteredDocuments(documents)
    } else if (isRefreshingAfterDelete) {
      // Clear table when deleting
      setFilteredDocuments([])
    }
  }, [documents, isRefreshing, isRefreshingAfterDelete])

  const cardDescription = useMemo(() => {
    return selectedDocument && category?.description
      ? category.description
      : 'Upload tax-specific documents here, such as prior year tax returns.'
  }, [selectedDocument, category])

  const onSearch = useCallback((data: DocumentSearchBarOnChangeData) => {
    setFilteredDocuments(data.filtered)
  }, [])

  const openModal = useCallback(() => {
    track('clicked document upload', {
      document_category: ModalTypes.TAX,
      document_subcategory: '',
    })
    dispatch?.(openUploadModal(ModalTypes.TAX))
  }, [dispatch, track])

  const { uploadDocuments, isUploading } = useDocumentUpload({
    documentCategory: 'tax',
    documentType: UploadDocumentType.TAX,
    year,
    userDocumentCategoryId:
      selectedDocument?.userDocumentCategoryId || userDocumentCategoryId,
    extraPayload: selectedDocument?.userDocumentCategoryId
      ? {
          userDocumentCategoryId: selectedDocument.userDocumentCategoryId,
        }
      : undefined,
    onUploadSuccess: async () => {
      // Clear the table immediately
      setIsRefreshing(true)
      setFilteredDocuments([])

      // Close modal
      dispatch?.(closeUploadModal())

      // Fetch fresh data
      await appDispatch(fetchUserDocuments(true, true))

      // Re-enable updates from props
      setIsRefreshing(false)
    },
    onUploadError: (errorMessage) => {
      console.error('Upload failed:', errorMessage)
    },
  })

  const handleDocumentsUpload = useCallback(
    async (documents: File[]) => {
      await uploadDocuments(documents)
    },
    [uploadDocuments]
  )

  return (
    <>
      <TabPaneToolbar
        documents={documents}
        onSearch={onSearch}
        rightElement={<UploadDocumentButton onClick={openModal} />}
      >
        <Text>
          Share tax-related documents here, such as prior-year tax returns.
        </Text>
      </TabPaneToolbar>

      <UserDocumentsTable
        columns={COLUMNS}
        documents={filteredDocuments}
        rowType={RowTypes.TAX}
        tableOptions={tableOptions}
        taxUserDocuments={taxUserDocuments}
      />

      <DocumentUploadDialog
        isOpen={activeUploadModal === ModalTypes.TAX}
        onOpenChange={(open) => {
          if (!open) {
            dispatch?.(closeUploadModal())
          }
        }}
        onClose={() => dispatch?.(closeUploadModal())}
        onUpload={handleDocumentsUpload}
        isUploadDisabled={isUploading}
        cardTitle={
          selectedDocument && category ? category.title : 'Tax Documents'
        }
        cardDescription={cardDescription}
      >
        <DocumentUploadDialog.CardContent>
          {!selectedDocument && (
            <DocumentCategorySelect
              userDocumentCategoryId={userDocumentCategoryId}
              setUserDocumentCategoryId={setUserDocumentCategoryId}
              documentCategoryType={UploadDocumentType.TAX}
            />
          )}

          <DatePicker
            placeholder="Select year"
            label="Tax Year"
            value={year?.toString() ?? ''}
            maxDate={new Date()}
            onChange={setYear}
            dateFormat={DATE_FORMATS_LUXON.YEAR_FOUR_DIGIT}
            showYearPicker
            clearable
            afterLabel={
              <Popup
                content={
                  <Text>
                    A tax year is the 12-month calendar year covered by a tax
                    return. In the U.S., the tax year for individuals runs from
                    Jan. 1 to Dec. 31 and includes taxes owed on earnings during
                    that period. For example, taxes withheld or owed for
                    earnings during the calendar year {currentTaxYear} would be
                    included on the tax return sent to the IRS in {nextTaxYear}.
                    Even though you are sending the tax return in {nextTaxYear},
                    it is for the tax year {currentTaxYear}.
                  </Text>
                }
              />
            }
          />
        </DocumentUploadDialog.CardContent>
      </DocumentUploadDialog>
    </>
  )
}

Tax.paneDescription =
  'Please use this section to upload any tax-specific documents, such as prior year tax returns.'
Tax.paneTitle = 'Taxes'
